import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import FullHeart from '@zola/zola-ui/src/assets/images/favorites/heart-full-no-outline.svg';
import EmptyHeart from '@zola/zola-ui/src/assets/images/favorites/heart-empty-dark-outline.svg';
import EmptyHeartHover from '@zola/zola-ui/src/assets/images/favorites/heart-empty-pink-outline.svg';
import { setCookie } from '@zola-helpers/client';
import { mapCardTypeToBusinessUnitComponent } from 'components/onboard/helpers';
import { getUserHasIdentified } from 'selectors/user/userSelectors';
import { AppDispatch } from 'reducers/index';
import { getFavoriteCardSuiteUUIDs, getAreFavoritesLoaded } from 'favorites/redux/selectors';
import {
  addFavoriteCardSuite,
  removeFavoriteCardSuite,
  fetchFavoriteCardSuiteUUIDs,
  openFavoritesModalIfNeeded,
} from 'favorites/redux/actions';
import { trackObjectUnfavorited, trackObjectFavorited } from 'favorites/utils/analytics';
import type { CARD_TYPES } from 'cards/components/CardListing/types';

import styles from './styles.module.less';

type FavoriteSuiteToggleProps = {
  className?: string;
  height?: number;
  suiteType: CARD_TYPES;
  suiteUUID: string;
};

export const FavoriteSuiteToggle: FC<FavoriteSuiteToggleProps> = ({
  className,
  height = 24,
  suiteType,
  suiteUUID,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userHasIdentified = useSelector(getUserHasIdentified);
  const favoriteSuiteUUIDs = useSelector(getFavoriteCardSuiteUUIDs);
  const areFavoritesLoaded = useSelector(getAreFavoritesLoaded);
  const isFavorite = useMemo<boolean>(() => favoriteSuiteUUIDs.includes(suiteUUID), [
    favoriteSuiteUUIDs,
    suiteUUID,
  ]);
  const [isAnimated, setIsAnimated] = useState(false);

  // If favorites haven't been loaded, fetch them! Note: don't worry about
  // multiple instances calling this = only a single API request will be made.
  useEffect(() => {
    if (!areFavoritesLoaded && userHasIdentified) dispatch(fetchFavoriteCardSuiteUUIDs());
  }, [areFavoritesLoaded, userHasIdentified, dispatch]);

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      const addFavorite = () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dispatch(addFavoriteCardSuite(suiteUUID));
        trackObjectFavorited({ type: suiteType, uuid: suiteUUID });
        setIsAnimated(true);
      };

      if (userHasIdentified) {
        if (isFavorite) {
          trackObjectUnfavorited({ type: suiteType, uuid: suiteUUID });
          dispatch(removeFavoriteCardSuite(suiteUUID));
        } else {
          addFavorite();
          dispatch(openFavoritesModalIfNeeded());
        }
      } else {
        setCookie('newUserPaperFavoriteSuiteUUID', suiteUUID);
        setCookie('newUserPaperFavoriteSuiteType', suiteType);
        setCookie('newUserPaperFavoriteLocation', window.location.pathname);
        const businessUnitComponent = mapCardTypeToBusinessUnitComponent(suiteType);

        setCookie('invitesOnboardFormData', suiteUUID);
        setCookie('invitesOnboardFormSource', 'favorites');
        window.location.href = `/wedding/onboard/wedding-paper/${businessUnitComponent}/form`;
      }
    },
    [dispatch, suiteType, suiteUUID, isFavorite, userHasIdentified]
  );

  // The zola-ui favorites SVG have a padding baked in, which we need to compensate for.
  const padding = Math.round((10 / 24) * height);
  const adjustedHeight = height + 2 * padding;
  const style = { margin: -padding };

  return (
    <div className={className}>
      <div className={styles.toggle} onClick={toggle} style={style}>
        {isFavorite ? (
          <img
            src={FullHeart}
            className={cx(isAnimated && styles.animation)}
            height={adjustedHeight}
            alt=""
          />
        ) : (
          <>
            <img src={EmptyHeart} height={adjustedHeight} className={styles.hideOnHover} alt="" />
            <img
              src={EmptyHeartHover}
              height={adjustedHeight}
              className={styles.showOnHover}
              alt=""
            />
          </>
        )}
      </div>
    </div>
  );
};
